import {
  Avatar,
  Badge,
  Button,
  Checkbox,
  Chip,
  Divider,
  Drawer,
  Fab,
  FormControlLabel,
  Grid,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  Zoom
} from '@material-ui/core';
import {
  Theme,
  createStyles,
  makeStyles,
  useTheme
} from '@material-ui/core/styles';
import {
  AddRounded,
  ClearRounded,
  CloseRounded,
  EditRounded,
  FilterListRounded,
  GroupRounded,
  MoreVertRounded,
  ShowChartRounded
} from '@material-ui/icons';
import { Alert, Autocomplete, Pagination } from '@material-ui/lab';
import clsx from 'clsx';
import { ReactNode, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link as RRLink, useHistory, useParams } from 'react-router-dom';
import SwipeableViews from 'react-swipeable-views';
import { RootState } from '../../app/store';
import { ORDER } from '../../common/enum/order';
import DocumentCard from '../../components/DocumentCard';
import EnhancedTableToolbar from '../../components/EnhancedTableToolbar';
import ProjectInfoCard from '../../components/ProjectInfoCard';
import TabPanel, { a11yProps } from '../../components/TabPanel';
import WorkflowCard from '../../components/WorkflowCard';
import displayRemovedUser from '../../functions/displayRemovedUser';
import getNotFoundText from '../../functions/getNotFoundText';
import { getCookie, updateCookie } from '../../functions/manageCookies';
import sortMembers from '../../functions/sortMembers';
import { stringAvatar } from '../../functions/stringAvatar';
import { useAppDispatch } from '../../hooks/useAppRudux';
import useDeviceScreen from '../../hooks/useDeviceScreen';
import { useQueryStringNumber } from '../../hooks/useQueryString';
import useProjectUsers from '../../hooks/useProjectUsers';
import Breadcrumb from '../Breadcrumb';
import { AccessControl } from '../MasterData/AccessControl/accessControlSlice';
import { MemberWithAccessControl } from '../ProjectAccessControlUser/ProjectAccessControlUserEdit';
import { getProjectApprovalPeriodsAsync } from '../ProjectApprovalPeriod/projectApprovalPeriodSlice';
import { getProjectDocumentsAsync } from '../ProjectDocument/projectDocumentSlice';
import { getProjectWorkflowsAsync } from '../ProjectWorkflow/projectWorkflowSlice';
import { User } from '../UserProfile/userProfileSlice';
import { getProjectAsync } from './projectSlice';

export interface DocumentParam {
  projectId?: number;
  page: number;
  limit: number;
  sortBy?: string;
  orderBy?: ORDER;
  disciplineId?: number[] | null; // The parameter of each user's computer can be `null`, causing `.length` of `disciplineId` is undefined
  documentCreatorIds?: number[];
  isDocumentCreator?: boolean; // Use for setting documentCreatorIds is logged-in user
  search?: string | null;
}

export interface WorkflowParam {
  projectId?: number;
  sortBy?: string;
  orderBy?: ORDER;
}

interface ProjectMember {
  userId: number;
  user?: User;
  accessControls: AccessControl[];
}

interface Fab {
  icon: ReactNode;
  label: string;
  to: string;
  color?: 'default';
  tabId: number;
}

interface FilterParam {
  disciplineId: number[];
  documentCreatorIds: number[];
  isDocumentCreator: boolean;
}

// Note: sortOptions will be created inside component to use translation

export default function ProjectView() {
  const classes = useStyles();
  const history = useHistory();
  const theme = useTheme();
  const { IS_MOBILE, IS_TABLET } = useDeviceScreen();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const sortDocOptions = [
    { value: 'id', text: t('projectView.sortOptions.createdDate') },
    { value: 'no', text: t('projectView.sortOptions.documentNo') },
    { value: 'name', text: t('projectView.sortOptions.documentName') }
  ];

  const sortFlowOptions = [
    { value: 'id', text: t('projectView.sortOptions.createdDate') },
    { value: 'name', text: t('projectView.sortOptions.flowName') }
  ];

  const { selectedProject } = useSelector((state: RootState) => state.projects);
  const { data: documents, count: countDocs } = useSelector(
    (state: RootState) => state.projectDocuments
  );
  const { data: flows } = useSelector(
    (state: RootState) => state.projectWorkflows
  );
  const { data: disciplines } = useSelector(
    (state: RootState) => state.disciplines
  );
  const { data: workflowSteps } = useSelector(
    (state: RootState) => state.workflowSteps
  );
  const { data: approvalPeriods } = useSelector(
    (state: RootState) => state.projectApprovalPeriods
  );
  const users = useProjectUsers(); // Environment-aware user selection
  const { user } = useSelector((state: RootState) => state.userProfile);

  const params = useParams<{ projectId: string }>();
  const projectId = Number.parseInt(params.projectId, 10);
  const tabId = useQueryStringNumber({ query: 'tabId' });

  const pageDocumentKey = `[DOC]-userId${user?.id}-projectId${projectId}-allDocuments`;
  const pageWorkflowKey = `[DOC]-userId${user?.id}-projectId${projectId}-allWorkflows`;
  const cookieDocumentParams: DocumentParam | undefined =
    getCookie(pageDocumentKey);
  const cookieWorkflowParams: WorkflowParam | undefined =
    getCookie(pageWorkflowKey);

  const defaultDocumentParams: DocumentParam = {
    projectId,
    page: 0,
    limit: 30,
    sortBy: 'id',
    orderBy: ORDER.desc,
    disciplineId: [],
    documentCreatorIds: [],
    isDocumentCreator: false,
    search: null
  };
  const defaultWorkflowParams: WorkflowParam = {
    projectId,
    sortBy: 'id',
    orderBy: ORDER.desc
  };

  const [tabValue, setTabValue] = useState<number>(0);
  const [documentParams, setDocumentParams] = useState<DocumentParam>({
    ...defaultDocumentParams,
    ...cookieDocumentParams
  });
  const [workflowParams, setWorkflowParams] = useState<WorkflowParam>({
    ...defaultWorkflowParams,
    ...cookieWorkflowParams
  });
  const [projectMembers, setProjectMembers] = useState<ProjectMember[]>([]);
  const [anchorElementMoreMenu, setAnchorElementMoreMenu] =
    useState<null | HTMLElement>(null);

  const [openFilterDrawer, setOpenFilterDrawer] = useState<boolean>(false);
  const [selectFilters, setSelectFilters] = useState<FilterParam>({
    disciplineId: [],
    documentCreatorIds: [],
    isDocumentCreator: false
  });
  const [canApplyFilter, setCanApplyFilter] = useState<boolean>(false);

  const filterCount =
    (documentParams.disciplineId?.length ?? 0) +
    (documentParams.documentCreatorIds?.length ?? 0);

  const showAlertAccess = !selectedProject?.users.some(
    ({ userId }) => userId === user?.id
  );

  const drawer = (
    <>
      <div className={classes.drawerHeader}>
        <Typography variant="h6">{t('projectView.documentFilter')}</Typography>
        <Tooltip title={t('common.close') as string}>
          <IconButton onClick={handleFilterDrawer(false)}>
            <CloseRounded />
          </IconButton>
        </Tooltip>
      </div>
      <Divider />
      <div className={classes.drawerContent}>
        <Typography variant="h6" gutterBottom>
          {t('projectView.documentCreator')}
        </Typography>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Autocomplete
              id="filter-member"
              size="small"
              multiple
              options={users.filter(
                (user) =>
                  !selectFilters.documentCreatorIds.some(
                    (documentCreatorId) => documentCreatorId === user.id
                  )
              )}
              getOptionLabel={(option) =>
                `${option?.firstName} ${option?.lastName}`
              }
              renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => (
                  <Chip
                    label={`${option?.firstName} ${option?.lastName}`}
                    {...getTagProps({ index })}
                    variant="outlined"
                    size="small"
                    deleteIcon={<ClearRounded />}
                  />
                ))
              }
              value={users.filter((user) =>
                selectFilters.documentCreatorIds.some(
                  (documentCreatorId) => documentCreatorId === user.id
                )
              )}
              getOptionSelected={(option, value) => option.id === value?.id}
              onChange={(event, newValue) => {
                const userIds = newValue.map((user) => user.id);
                handleChangeDropdownFilter(userIds, 'documentCreatorIds');
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder={
                    selectFilters.documentCreatorIds.length === 0
                      ? (t('projectView.filterByDocumentCreator') as string)
                      : ''
                  }
                />
              )}
              disabled={selectFilters.isDocumentCreator}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectFilters.isDocumentCreator}
                  onChange={(event) =>
                    handleChangeCheckFilter(
                      event.target.checked,
                      'isDocumentCreator'
                    )
                  }
                  color="primary"
                />
              }
              label={t('projectView.showOnlyMyDocument')}
            />
          </Grid>
        </Grid>
        <Typography variant="h6">{t('projectView.discipline')}</Typography>
        <Grid container>
          {disciplines.map((discipline) => {
            const ref = 'disciplineId';
            const params = selectFilters[ref];
            const isChecked: boolean = params
              ? params.some((id) => id === discipline.id)
              : false;
            return (
              <Grid item xs={6} key={discipline.id}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isChecked}
                      onChange={(event) =>
                        handleChangeCheckFilter(
                          event.target.checked,
                          ref,
                          discipline.id
                        )
                      }
                      color="primary"
                    />
                  }
                  label={discipline.name}
                />
              </Grid>
            );
          })}
        </Grid>
      </div>
      <div className={classes.drawerAction}>
        <Button
          variant="outlined"
          color="primary"
          fullWidth
          onClick={handleClearFilter}
        >
          {t('common.clear')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="large"
          fullWidth
          onClick={handleLoadingFilter}
          disabled={!canApplyFilter}
        >
          {t('common.apply')}
        </Button>
      </div>
    </>
  );

  const transitionDuration = {
    enter: theme.transitions.duration.enteringScreen,
    exit: 0 //theme.transitions.duration.leavingScreen
  };

  const fabs: Fab[] = [
    {
      icon: <AddRounded className={classes.extendedIcon} />,
      label: t('projectView.newDocument'),
      to: `/documents/create?projectId=${projectId}`,
      color: 'default',
      tabId: 0
    },
    {
      icon: <AddRounded className={classes.extendedIcon} />,
      label: t('projectView.bulkPlanningDocuments'),
      to: `/documents/bulk-planning?projectId=${projectId}`,
      tabId: 0
    },
    {
      icon: <AddRounded className={classes.extendedIcon} />,
      label: t('projectView.newProjectWorkflow'),
      to: `/project-workflows/create?projectId=${projectId}`,
      tabId: 1
    }
  ];

  // Prevent loading all project information at one time and reload information when changing tab
  useEffect(() => {
    switch (tabValue) {
      case 0:
        if (!tabId) loadingDocument(documentParams);
        break;
      case 1:
        loadingFlow(workflowParams);
        break;
      case 2:
        dispatch(getProjectApprovalPeriodsAsync(projectId));
        break;
    }
  }, [tabValue]);

  // For displaying project users (NOT ALL) which have access controls
  // NOTE: some user can be `undefined` if the user was removed from the `space PROJECT`
  useEffect(() => {
    if (selectedProject) {
      const projectMembers = selectedProject.users.map(
        (documentManagementUser) => {
          const projectRegisterUser = users.find(
            (projectRegisterUser) =>
              projectRegisterUser.id === documentManagementUser.userId
          );
          return {
            userId: documentManagementUser.userId,
            user: projectRegisterUser,
            accessControls: documentManagementUser.accessControls
          };
        }
      );
      setProjectMembers(projectMembers);
    }
  }, [selectedProject, users]);

  // For setting default of document filter
  useEffect(() => {
    if (cookieDocumentParams && user) {
      let disciplineId: number[] = [];
      let documentCreatorIds: number[] = [];
      let isDocumentCreator: boolean = false;
      if (cookieDocumentParams?.disciplineId) {
        disciplineId = cookieDocumentParams.disciplineId;
      }
      if (cookieDocumentParams?.isDocumentCreator) {
        documentCreatorIds = [user.id];
        isDocumentCreator = true;
      } else if (cookieDocumentParams?.documentCreatorIds) {
        documentCreatorIds = cookieDocumentParams.documentCreatorIds;
      }
      setSelectFilters({
        ...selectFilters,
        disciplineId,
        documentCreatorIds,
        isDocumentCreator
      });
    }
  }, [user]);

  // For fetching project information and navigating to selected tab
  useEffect(() => {
    dispatch(getProjectAsync(projectId));
    if (tabId) {
      setTabValue(tabId);
    } else {
      setTabValue(0);
    }
  }, []);

  function handleTabChange(event: React.ChangeEvent<{}>, newValue: number) {
    if (tabValue !== newValue) {
      setTabValue(newValue);
      if (newValue === 0) {
        history.push(`/projects/${projectId}`);
      } else {
        history.push(`/projects/${projectId}?tabId=${newValue}`);
      }
    }
  }

  function handleChangeIndex(index: number) {
    if (tabValue !== index) {
      setTabValue(index);
      if (index === 0) {
        history.push(`/projects/${projectId}`);
      } else {
        history.push(`/projects/${projectId}?tabId=${index}`);
      }
    }
  }

  function loadingDocument(documentParam: DocumentParam) {
    updateCookie(pageDocumentKey, documentParam);
    dispatch(getProjectDocumentsAsync(documentParam));
    setDocumentParams(documentParam);
  }

  function loadingFlow(workflowParam: WorkflowParam) {
    updateCookie(pageWorkflowKey, workflowParam);
    dispatch(getProjectWorkflowsAsync(workflowParam));
    setWorkflowParams(workflowParam);
  }

  function handleChangePage<T>(
    event: React.ChangeEvent<unknown>,
    newPage: number,
    params: T,
    loadingElement: (params: T) => void
  ) {
    loadingElement({
      ...params,
      page: newPage - 1 //The first value of pagination is 1, not 0
    });
  }

  function handleFilterDrawer(open: boolean) {
    return (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event.type === 'keydown' &&
        ((event as React.KeyboardEvent).key === 'Tab' ||
          (event as React.KeyboardEvent).key === 'Shift')
      ) {
        return;
      }
      setOpenFilterDrawer(open);
    };
  }

  function handleChangeCheckFilter(
    checked: boolean,
    field: 'disciplineId' | 'isDocumentCreator',
    id?: number
  ) {
    if (field === 'disciplineId' && id) {
      // For `disciplineId` Checkbox filter
      const selectedParams = selectFilters[field];
      if (checked) {
        setSelectFilters({
          ...selectFilters,
          [field]: [...selectedParams, id]
        });
      } else {
        const newParams = selectedParams.filter((value) => value != id);
        setSelectFilters({ ...selectFilters, [field]: newParams });
      }
    } else if (field === 'isDocumentCreator') {
      // For `isDocumentCreator` Checkbox filter
      if (user) {
        if (checked) {
          setSelectFilters({
            ...selectFilters,
            documentCreatorIds: [user.id],
            [field]: checked
          });
        } else {
          setSelectFilters({
            ...selectFilters,
            documentCreatorIds: [],
            [field]: checked
          });
        }
      }
    }
    setCanApplyFilter(true);
  }

  function handleChangeDropdownFilter(
    value: number[],
    field: 'documentCreatorIds'
  ) {
    if (field === 'documentCreatorIds') {
      setSelectFilters({ ...selectFilters, [field]: value });
    }
    setCanApplyFilter(true);
  }

  function handleClearFilter() {
    loadingDocument({
      ...documentParams,
      page: 0,
      disciplineId: [],
      documentCreatorIds: [],
      isDocumentCreator: false,
      search: null
    });
    setSelectFilters({
      ...selectFilters,
      disciplineId: [],
      documentCreatorIds: [],
      isDocumentCreator: false
    });
    setOpenFilterDrawer(false);
    setCanApplyFilter(false);
  }

  function handleLoadingFilter() {
    loadingDocument({
      ...documentParams,
      ...selectFilters,
      page: 0
    });
    setOpenFilterDrawer(false);
    setCanApplyFilter(false);
  }

  function handleClickMoreMenu(event: React.MouseEvent<HTMLElement>) {
    setAnchorElementMoreMenu(event.currentTarget);
  }

  function handleCloseMoreMenu() {
    setAnchorElementMoreMenu(null);
  }

  return (
    <div className={classes.root}>
      <Breadcrumb
        name="projectName"
        data={{
          projectName: selectedProject?.name,
          projectId: selectedProject?.id
        }}
      />
      <div className={classes.titleBox}>
        <Typography component="p" variant="h4">
          {t('projectView.projectDetails')}
        </Typography>
        <div className={classes.flexGrow} />
        <Button
          component={RRLink}
          to={`/projects/${projectId}/dashboards`}
          variant="contained"
          color="primary"
          size="small"
          startIcon={<ShowChartRounded />}
        >
          {t('projectView.projectDashboard')}
        </Button>
      </div>
      <Grid container spacing={2}>
        {!IS_TABLET && (
          <Grid item xs={12} md={3}>
            {selectedProject && (
              <ProjectInfoCard project={selectedProject} showSpaceConnect />
            )}
          </Grid>
        )}
        <Grid item xs={12} md={9}>
          <Tabs
            className={classes.tabs}
            value={tabValue}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
            scrollButtons="auto"
          >
            <Tab className={classes.tab} label={t('projectView.documents')} {...a11yProps(0)} />
            <Tab
              className={classes.tab}
              label={t('projectView.projectWorkflows')}
              {...a11yProps(1)}
            />
            <Tab className={classes.tab} label={t('projectView.settings')} {...a11yProps(2)} />
          </Tabs>
          <SwipeableViews
            axis="x"
            index={tabValue}
            onChangeIndex={handleChangeIndex}
          >
            <TabPanel value={tabValue} index={0}>
              {showAlertAccess && (
                <Alert severity="info">
                  {t('projectView.noAccessAlert')}
                </Alert>
              )}
              <div
                className={clsx(classes.flexAlignCenter, {
                  [classes.paddingRight1]: filterCount > 0
                })}
              >
                <div className={classes.flexGrow}>
                  <EnhancedTableToolbar<DocumentParam>
                    name={t('projectView.documents')}
                    params={documentParams}
                    sortOptions={sortDocOptions}
                    loadingElement={loadingDocument}
                    canSearchable
                  />
                </div>
                <Tooltip title={t('common.filter') as string} arrow>
                  <Badge
                    badgeContent={filterCount}
                    color="primary"
                    overlap="circle"
                  >
                    <IconButton
                      className={classes.marginBottom1}
                      size="small"
                      onClick={handleFilterDrawer(true)}
                    >
                      <FilterListRounded />
                    </IconButton>
                  </Badge>
                </Tooltip>
                <IconButton
                  className={classes.marginBottom1}
                  size="small"
                  onClick={handleClickMoreMenu}
                >
                  <MoreVertRounded />
                </IconButton>
              </div>
              <Typography
                className={classes.marginBottom1}
                variant="body2"
                color="textSecondary"
              >
                {t('common.about')} {countDocs} {countDocs > 1 ? t('common.results') : t('common.result')}
              </Typography>
              {documents.length > 0 ? (
                <DocumentCard<DocumentParam>
                  renderDocuments={documents}
                  params={documentParams}
                  setParams={setDocumentParams}
                />
              ) : (
                <div className={classes.fileNotFound}>
                  {getNotFoundText(
                    documentParams.search,
                    t('common.document') as string,
                    t('common.documents') as string,
                    t
                  )}
                </div>
              )}
              <div className={classes.pagination}>
                <Pagination
                  count={Math.ceil(countDocs / documentParams.limit)}
                  shape="rounded"
                  page={documentParams.page + 1}
                  onChange={(event, newPage) => {
                    handleChangePage<DocumentParam>(
                      event,
                      newPage,
                      documentParams,
                      loadingDocument
                    );
                  }}
                />
              </div>
            </TabPanel>
            <TabPanel value={tabValue} index={1}>
              <div className={classes.marginBottom1}>
                <EnhancedTableToolbar<WorkflowParam>
                  name={t('projectView.projectWorkflows')}
                  params={workflowParams}
                  sortOptions={sortFlowOptions}
                  loadingElement={loadingFlow}
                />
                {flows.length > 0 ? (
                  <WorkflowCard<WorkflowParam>
                    renderFlows={flows}
                    params={workflowParams}
                    loadingElement={loadingFlow}
                  />
                ) : (
                  <div className={classes.fileNotFound}>
                    {t('projectView.projectWorkflowNotFound')}
                  </div>
                )}
              </div>
            </TabPanel>
            <TabPanel value={tabValue} index={2}>
              <div className={classes.marginBottom1}>
                <Typography
                  className={classes.tabHeader}
                  variant="body1"
                  color="textSecondary"
                >
                  {t('projectView.approvalPeriod')}
                </Typography>
                <Paper className={classes.paper}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      {workflowSteps.map((workflowStep) => {
                        const approvalPeriod = approvalPeriods.find(
                          (approvalPeriod) =>
                            approvalPeriod.workflowStepId === workflowStep.id
                        );
                        return (
                          <div
                            key={workflowStep.name}
                            className={clsx({
                              [classes.disabledColor]: !approvalPeriod
                            })}
                          >
                            <div className={classes.positionText}>
                              <Typography className={classes.flexGrow}>
                                {workflowStep.name}
                              </Typography>
                              <Typography>
                                {approvalPeriod ? approvalPeriod?.value : '-'}{' '}
                                {t('projectView.days')}
                              </Typography>
                            </div>
                          </div>
                        );
                      })}
                      <Button
                        component={RRLink}
                        to={`/projects/${projectId}/edit`}
                        color="primary"
                        variant="outlined"
                        fullWidth
                        startIcon={<EditRounded />}
                      >
                        {t('projectView.editApprovalPeriod')}
                      </Button>
                    </Grid>
                  </Grid>
                </Paper>
                <Typography
                  className={clsx(classes.tabHeader, classes.marginTop1)}
                  variant="body1"
                  color="textSecondary"
                >
                  {t('projectView.projectMembers')}
                </Typography>
                <Paper className={classes.paper}>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      {sortMembers<MemberWithAccessControl>(projectMembers).map(
                        (documentManagementUser) => {
                          const removedUser = !documentManagementUser.user;
                          const user = users.find(
                            ({ id }) => id === documentManagementUser.userId
                          );
                          const notHasPermission = user?.roles?.length === 0;
                          return (
                            <Grid
                              className={classes.displayMember}
                              container
                              key={documentManagementUser.userId}
                            >
                              <Grid
                                className={classes.flexAlignCenter}
                                item
                                xs={12}
                                sm={5}
                              >
                                <Avatar
                                  {...stringAvatar(
                                    displayRemovedUser(
                                      documentManagementUser.userId,
                                      documentManagementUser.user
                                    )
                                  )}
                                />
                                <div className={classes.styleUserName}>
                                  <Typography
                                    className={classes.lineHeight120}
                                    color={
                                      removedUser
                                        ? 'textSecondary'
                                        : 'textPrimary'
                                    }
                                  >
                                    {displayRemovedUser(
                                      documentManagementUser.userId,
                                      documentManagementUser.user
                                    )}
                                  </Typography>
                                  {notHasPermission && (
                                    <Typography
                                      className={classes.lineHeight120}
                                      variant="caption"
                                      color="error"
                                    >
                                      🚫 {t('projectView.notAuthorized')}
                                    </Typography>
                                  )}
                                </div>
                              </Grid>
                              <Grid
                                className={clsx(
                                  classes.flexAlignCenter,
                                  classes.styleAccessControl
                                )}
                                item
                                xs={12}
                                sm={7}
                              >
                                {documentManagementUser.accessControls.map(
                                  (accessControl) => {
                                    return (
                                      <Tooltip
                                        key={accessControl.id}
                                        title={t('projectView.hasAccessToLevel', { level: accessControl.name.toLowerCase() }) as string}
                                        arrow
                                      >
                                        <Chip
                                          className={classes.chipIcon}
                                          key={accessControl.id}
                                          variant="outlined"
                                          size="small"
                                          label={accessControl.name}
                                        />
                                      </Tooltip>
                                    );
                                  }
                                )}
                              </Grid>
                            </Grid>
                          );
                        }
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6} style={{ paddingTop: 0 }}>
                      <Button
                        component={RRLink}
                        to={`/projects/${projectId}/members`}
                        color="primary"
                        variant="outlined"
                        fullWidth
                        startIcon={<GroupRounded />}
                      >
                        {t('projectView.manageMembers')}
                      </Button>
                    </Grid>
                  </Grid>
                </Paper>
              </div>
            </TabPanel>
          </SwipeableViews>
        </Grid>
      </Grid>
      <Drawer
        className={clsx(classes.drawerDesktop, {
          [classes.drawerMobile]: IS_MOBILE
        })}
        anchor={!IS_MOBILE ? 'right' : 'bottom'}
        open={openFilterDrawer}
        onClose={handleFilterDrawer(false)}
        role="presentation"
      >
        {drawer}
      </Drawer>
      <Menu
        anchorEl={anchorElementMoreMenu}
        keepMounted
        open={Boolean(anchorElementMoreMenu)}
        onClose={handleCloseMoreMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        <MenuItem component={RRLink} to={`/projects/${projectId}/history`}>
          <ListItemText primary={t('projectView.history')} />
        </MenuItem>
      </Menu>
      <div className={classes.fab}>
        <>
          {fabs.map((fab, index) => (
            <Zoom
              key={index}
              in={tabValue === fab.tabId}
              timeout={transitionDuration}
              style={{
                transitionDelay: `${tabValue === fab.tabId ? transitionDuration.exit : 0
                  }ms`
              }}
              unmountOnExit
            >
              <Fab
                className={clsx({
                  [classes.defaultFabButton]: fab.color === 'default'
                })}
                component={RRLink}
                to={fab.to}
                variant="extended"
                color={fab.color ?? 'primary'}
                aria-label={fab.label}
              >
                {fab.icon}
                {fab.label}
              </Fab>
            </Zoom>
          ))}
        </>
      </div>
    </div>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: theme.spacing(1.5, 0, 12, 0),
      [theme.breakpoints.down('xs')]: {
        marginBottom: theme.spacing(19)
      }
    },
    titleBox: {
      display: 'flex',
      alignItems: 'center',
      margin: theme.spacing(1.5, 0, 2, 0),
      [theme.breakpoints.down('xs')]: {
        display: 'block',
        textAlign: 'center',
        margin: theme.spacing(3.5, 0, 4, 0)
      }
    },
    tabs: {
      marginBottom: theme.spacing(1),
      borderBottomStyle: 'solid',
      borderWidth: '1px',
      borderColor: theme.palette.divider
    },
    tab: {
      lineHeight: '120%'
    },
    fileNotFound: {
      padding: theme.spacing(10, 2),
      textAlign: 'center'
    },
    pagination: {
      display: 'flex',
      justifyContent: 'flex-end',
      marginTop: theme.spacing(1.5)
    },
    fab: {
      position: 'fixed',
      bottom: theme.spacing(3),
      right: theme.spacing(3),
      zIndex: 100,
      display: 'flex',
      gap: theme.spacing(1),
      [theme.breakpoints.down('xs')]: {
        flexDirection: 'column'
      }
    },
    extendedIcon: {
      marginRight: theme.spacing(1)
    },
    flexAlignCenter: {
      display: 'flex',
      alignItems: 'center'
    },
    flexGrow: {
      flexGrow: 1
    },
    marginIconButton: {
      margin: theme.spacing(-1, 0, -1, 1)
    },
    marginTop1: {
      marginTop: theme.spacing(1)
    },
    marginBottom1: {
      marginBottom: theme.spacing(1)
    },
    marginBottom2: {
      marginBottom: theme.spacing(2)
    },
    paddingRight1: {
      paddingRight: theme.spacing(1)
    },
    paper: {
      borderRadius: 16,
      padding: theme.spacing(2)
    },
    positionText: {
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(0, 0.5),
      marginBottom: theme.spacing(1)
    },
    textAlignCenter: {
      textAlign: 'center'
    },
    divider: {
      margin: theme.spacing(0, 2)
    },
    tabHeader: {
      padding: theme.spacing(1.4, 1)
    },
    drawerDesktop: {
      height: '100%',
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      '& .MuiDrawer-paper': {
        boxSizing: 'border-box',
        width: 400,
        borderRadius: theme.spacing(2, 0, 0, 2)
      }
    },
    drawerMobile: {
      '& .MuiDrawer-paper': {
        boxSizing: 'border-box',
        width: '100%',
        borderRadius: theme.spacing(2, 2, 0, 0)
      }
    },
    drawerHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: theme.spacing(0, 1, 0, 3),
      // necessary for content to be below app bar
      ...theme.mixins.toolbar
    },
    drawerContent: {
      marginBottom: 120, // For spare bottom space for action button
      padding: theme.spacing(1, 3),
      flexGrow: 1
    },
    drawerAction: {
      position: 'fixed',
      width: 400,
      right: 0,
      bottom: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1),
      padding: theme.spacing(2, 2, 2, 2),
      backgroundColor: theme.palette.background.paper,
      borderBottomLeftRadius: theme.spacing(2),
      borderTopStyle: 'solid',
      borderWidth: 'thin',
      borderColor: theme.palette.divider,
      zIndex: 2,
      [theme.breakpoints.down('xs')]: {
        width: '100%',
        borderBottomLeftRadius: 0
      }
    },
    disabledColor: {
      color: theme.palette.text.disabled
    },
    lineHeight120: {
      lineHeight: '120%'
    },
    displayMember: {
      marginBottom: theme.spacing(1),
      '&:last-child': {
        marginBottom: 0
      }
    },
    styleUserName: {
      flexGrow: 1,
      marginLeft: theme.spacing(1),
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(-1)
    },
    chipIcon: {
      margin: theme.spacing(0.125, 0.5, 0.125, 0)
    },
    styleAccessControl: {
      flexWrap: 'wrap',
      [theme.breakpoints.down('xs')]: {
        paddingLeft: theme.spacing(4.5)
      }
    },
    defaultFabButton: {
      backgroundColor:
        theme.palette.type === 'dark'
          ? theme.palette.text.primary
          : theme.palette.background.paper
    }
  })
);
